/* Package imports */
import axiosInstance from '../axiosInstance';
import { authActions } from '../store/auth/auth';
import { GlobalValues } from "../constants/global";
import { showErrorToast, showInfoToast } from './toast';
import { extractServerMessage } from "./serverMessage";

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach((prom) => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

/**
 * Setup axios interceptors for auto-logout on token expiration
 * @param {object} store - Redux store instance
 */
export const setupAxiosInterceptors = (store) => {
    // Request interceptor - Add auth token to requests
    axiosInstance.interceptors.request.use(
        (config) => {
            const state = store.getState();
            const token = state.auth.token;
            
            if (token) {
                config.headers.Authorization = `Bearer ${token}`;
            }
            
            return config;
        },
        (error) => {
            return Promise.reject(error);
        }
    );

    // Response interceptor - Refresh access token on expiration
    axiosInstance.interceptors.response.use(
        (response) => {
            return response;
        },
        async (error) => {
            const status = error?.response?.status;
            const originalRequest = error?.config || {};
            const refreshPath = GlobalValues.refreshTokenUrl;
            const logoutPath = GlobalValues.logoutUrl;
            const isRefreshRequest = String(originalRequest?.url || "").includes(refreshPath);
            const isLogoutRequest = String(originalRequest?.url || "").includes(logoutPath);

            // Manual logout should never show "session expired" toast or force redirect.
            if (status === 401 && isLogoutRequest) {
                store.dispatch(authActions.logout());
                return Promise.reject(error);
            }

            if (status === 401 && !isRefreshRequest && !originalRequest._retry) {
                const refreshToken = store.getState()?.auth?.refreshToken;
                if (!refreshToken) {
                    store.dispatch(authActions.logout());
                    showErrorToast('Your session has expired. Please login again.');
                    if (window.location.pathname !== '/account/login') {
                        setTimeout(() => {
                            window.location.href = '/account/login';
                        }, 500);
                    }
                    return Promise.reject(error);
                }

                if (isRefreshing) {
                    return new Promise((resolve, reject) => {
                        failedQueue.push({ resolve, reject });
                    })
                        .then((newToken) => {
                            originalRequest.headers = originalRequest.headers || {};
                            originalRequest.headers.Authorization = `Bearer ${newToken}`;
                            return axiosInstance(originalRequest);
                        })
                        .catch((err) => Promise.reject(err));
                }

                originalRequest._retry = true;
                isRefreshing = true;

                try {
                    const refreshResponse = await axiosInstance.post(refreshPath, {
                        refresh: refreshToken
                    });
                    const newAccessToken = refreshResponse?.data?.access;

                    if (!newAccessToken) {
                        throw new Error("Missing access token in refresh response.");
                    }

                    store.dispatch(authActions.updateAccessToken(newAccessToken));
                    processQueue(null, newAccessToken);

                    originalRequest.headers = originalRequest.headers || {};
                    originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
                    return axiosInstance(originalRequest);
                } catch (refreshError) {
                    processQueue(refreshError, null);
                    store.dispatch(authActions.logout());
                    showErrorToast('Your session has expired. Please login again.');
                    if (window.location.pathname !== '/account/login') {
                        setTimeout(() => {
                            window.location.href = '/account/login';
                        }, 500);
                    }
                    return Promise.reject(refreshError);
                } finally {
                    isRefreshing = false;
                }
            }

            // If refresh request itself fails with 401, force logout.
            if (status === 401 && isRefreshRequest) {
                store.dispatch(authActions.logout());
                showErrorToast('Your session has expired. Please login again.');
                if (window.location.pathname !== '/account/login') {
                    setTimeout(() => {
                        window.location.href = '/account/login';
                    }, 500);
                }
                return Promise.reject(error);
            }

            // Global: treat 4xx as informational toasts with server message (no blocking modal)
            if (status >= 400 && status < 500) {
                // For 404 we often receive an HTML error page; don't surface server payload.
                if (status === 404) {
                    showInfoToast("Not found.");
                    return Promise.reject(error);
                }

                const serverMessage = extractServerMessage(error?.response?.data);
                const fallback = "Request could not be processed.";
                showInfoToast(serverMessage || fallback);
                return Promise.reject(error);
            }

            return Promise.reject(error);
        }
    );
};
